import { useQuery } from '@tanstack/react-query'
import { listKbFolders } from '@/api/kb'
import { useMeuRobo, usePedidosDeAnalise } from '@/api/robo'
import { acessoDaKb } from '@/pages/conhecimento/acesso'
import type { CriterioDeArea } from '@/changelogAreas'
import { useAuthStore, useCan, useCurrentTenant } from '@/store/auth'
import { useManagedOrganizations } from '@/hooks/useOrganizations'
import { useOrganizacoesWagw } from '@/hooks/useWhatsappGateways'
import { useOrganizacoesWhatsApi } from '@/hooks/useEscopoWhatsApi'

export interface CriteriosDeArea {
  /** `false` enquanto alguma consulta de que um critério depende ainda está em curso. */
  carregado: boolean
  /** Critérios cuja consulta FALHOU: o valor em `criterios` é o puro (false por falta de dado). */
  criteriosComErro: Set<CriterioDeArea>
  criterios: Record<CriterioDeArea, boolean>
}

/**
 * Os booleanos que a Sidebar usa para telas decididas por `hidden:` calculado
 * (Dashboard, Base de Conhecimento, Meu robô, Pedidos de análise, Organizações,
 * Gateways WhatsApp, Mensagens do sistema), num lugar só — para o menu e para o
 * filtro de novidades (spec 2026-10-27-novidades-por-perfil §5) não divergirem.
 *
 * `criterios` é puro: consulta que falhou fica `false`, e o menu esconde o item como
 * sempre fez. O filtro de novidades é que trata o critério em `criteriosComErro` como
 * visível (não saber não pode esconder novidade; errar para esse lado custa um aviso a
 * mais, nunca um a menos).
 * Sem usuário, nenhuma consulta dispara e `carregado` é `true` (ninguém para avisar).
 */
export function useCriteriosDeArea(): CriteriosDeArea {
  const user = useAuthStore((s) => s.user)
  const currentTenant = useCurrentTenant()
  const can = useCan()

  // Organizações: admin (fluxo atual) OU quem gere >= 1 org.
  const gestor = useManagedOrganizations()
  const managedOrgs = gestor.data ?? []
  const organizacoes = can('admin', 'platform') || managedOrgs.length > 0

  // Gateways WhatsApp: whatsapp_gateway:view em alguma org, decidido pelo backend.
  const wagw = useOrganizacoesWagw()
  const gatewaysWhatsapp = (wagw.data ?? []).length > 0

  // Base de Conhecimento: ao menos uma pasta navegável, ou gere a KB, ou admin.
  const pastas = useQuery({
    queryKey: ['kb-folders'],
    queryFn: () => listKbFolders(),
    staleTime: 60_000,
    retry: false,
    enabled: !!user,
  })
  const baseDeConhecimento = (pastas.data ?? []).length > 0 || acessoDaKb(user).gere

  // Conversas do WhatsApp: só no modo organização (spec 2026-11-02 conversas §5.7) —
  // `whatsapi:read` em alguma organização, decidido pelo backend. Nenhuma área de novidade
  // depende dele (a do WhatsApi cobre o grupo), então não entra em `carregado` nem nos erros.
  const orgsWhatsApi = useOrganizacoesWhatsApi({ enabled: !!user })
  const conversasWhatsapi = (orgsWhatsApi.data ?? []).length > 0

  // Mensagens do sistema: permissão dedicada OU admin OU gestor de >= 1 org.
  const mensagensDoSistema =
    can('notificacoes', 'template_write') || can('admin', 'platform') ||
    managedOrgs.length > 0

  // Meu robô: posse, não permissão.
  const meu = useMeuRobo({ enabled: !!user })
  const meuRobo = !!meu.robo

  // Pedidos de análise: só quem tem pedido para pegar.
  const pedidos = usePedidosDeAnalise({ enabled: !!user })
  const pedidosDeAnalise = (pedidos.data ?? []).length > 0

  const tid = currentTenant?.id ?? null
  const dashboard =
    can('recursos', 'read', tid) ||
    can('chamados', 'read', tid) ||
    can('finops', 'read', tid) ||
    can('audit', 'read', tid) ||
    can('jira', 'read', tid) ||
    can('dashboard', 'read', tid)

  const carregado = !(
    gestor.isLoading || wagw.isLoading || pastas.isLoading || meu.carregando || pedidos.isLoading
  )

  const criteriosComErro = new Set<CriterioDeArea>()
  if (gestor.isError) { criteriosComErro.add('organizacoes'); criteriosComErro.add('mensagensDoSistema') }
  if (wagw.isError) criteriosComErro.add('gatewaysWhatsapp')
  if (pastas.isError) criteriosComErro.add('baseDeConhecimento')
  if (meu.erro) criteriosComErro.add('meuRobo')
  if (pedidos.isError) criteriosComErro.add('pedidosDeAnalise')

  return {
    carregado,
    criteriosComErro,
    criterios: {
      organizacoes, gatewaysWhatsapp, meuRobo, pedidosDeAnalise,
      baseDeConhecimento, dashboard, mensagensDoSistema, conversasWhatsapi,
    },
  }
}
